import { Box, Button, Chip, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { StatusOption } from '@sharedInterfaces/ICompany';
import type Opportunity from '@src/Objects/Opportunity';
import { AppState } from '@store/store';
import { Briefcase, Calendar, Eye, MapPin, Users } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface MvpOpportunityCardProps {
    opportunity: Opportunity;
    statusOptionsMap: Map<string, StatusOption>;
}

/**
 * Modern Opportunity Card component matching reference design
 */
function MvpOpportunityCard({ opportunity, statusOptionsMap }: MvpOpportunityCardProps) {
    const navigate = useNavigate();
    const theme = useTheme();

    const statusOption = statusOptionsMap.get(opportunity.status);
    const skillScore = opportunity.selectedEmployees.length
        ? Math.round(
              (opportunity.selectedEmployees.reduce((prev, cur) => cur.score + prev, 0) /
                  opportunity.selectedEmployees.length) *
                  100,
          )
        : 0;

    const handleCardClick = () => {
        navigate(`/${ELinks.SALES_OPPORTUNITIES}/${opportunity.id}`);
    };

    // TODO: DEVELOPER - Backend Integration needed:
    // 1. Add 'category' field to IOpportunity interface
    // 2. Add to GraphQL schema (Opportunity type)
    // 3. Add to database schema (opportunities table)
    // 4. Create category management in settings
    // 5. Include in create/update mutations
    // 6. Add to AI analysis for auto-generation
    const getCategoryDisplay = (categoryValue: string) => {
        const categoryMap: Record<string, string> = {
            development: 'Entwicklung',
            consulting: 'Beratung',
            design: 'Design',
            testing: 'Testing',
            management: 'Management',
            support: 'Support',
        };
        return categoryMap[categoryValue] || categoryValue || 'Allgemein';
    };

    const categoryValue = (opportunity as any).category;
    const categoryDisplay = getCategoryDisplay(categoryValue);

    return (
        <Paper
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                mb: 2,
                borderRadius: 3,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                },
            }}
        >
            {/* Header: Title + Badges */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        fontSize: { xs: '1.125rem', sm: '1.25rem' },
                        lineHeight: 1.3,
                        flex: 1,
                        mr: 2,
                    }}
                >
                    {opportunity.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        label={categoryDisplay}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderRadius: 6,
                        }}
                    />
                    <Chip
                        label={statusOption?.title || 'Aktiv'}
                        sx={{
                            bgcolor: theme.palette.success.light,
                            color: theme.palette.success.contrastText,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                        }}
                    />
                </Box>
            </Box>

            {/* Customer + Partner */}
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5, fontSize: '1rem' }}>
                {opportunity.customer || 'DataInsights GmbH'}
                {opportunity.partner && (
                    <>
                        <br />
                        Partner: {opportunity.partner}
                    </>
                )}
            </Typography>

            {/* Short Description */}
            {/* TODO: DEVELOPER - Backend Integration needed:
                1. Add 'shortDescription' field to IOpportunity interface
                2. Add to GraphQL schema (Opportunity type)
                3. Add to database schema (opportunities table)
                4. Include in create/update mutations
                5. Add to AI analysis for auto-generation
            */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
                {(opportunity as any).shortDescription || 'Keine Beschreibung verfügbar'}
            </Typography>

            {/* Details Grid */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                    mb: 3,
                }}
            >
                {/* Frist */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Calendar size={16} color={theme.palette.primary.main} />
                    <Typography variant="body2" color="text.secondary">
                        {opportunity.deadline ? opportunity.deadline.toLocaleDateString('de-DE') : '10.3.2025'}
                    </Typography>
                </Box>

                {/* Mitarbeitende */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Users size={16} color={theme.palette.primary.main} />
                    <Typography variant="body2" color="text.secondary">
                        {opportunity.headCount || 3} Mitarbeiter
                    </Typography>
                </Box>

                {/* Remote */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MapPin size={16} color={theme.palette.primary.main} />
                    <Typography variant="body2" color="text.secondary">
                        {opportunity.remote ? `${Math.round(opportunity.remote)}% Remote` : 'Remote'}
                    </Typography>
                </Box>

                {/* Hauptrolle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Briefcase size={16} color={theme.palette.primary.main} />
                    <Typography variant="body2" color="text.secondary">
                        {opportunity.primaryRole?.title || '11 months'}
                    </Typography>
                </Box>
            </Box>

            {/* Details Button */}
            <Button
                variant="outlined"
                fullWidth
                startIcon={<Eye size={16} />}
                onClick={handleCardClick}
                sx={{
                    borderRadius: 2,
                    py: 1.5,
                    fontWeight: 500,
                    textTransform: 'none',
                }}
            >
                Details
            </Button>
        </Paper>
    );
}

export default MvpOpportunityCard;
